import { Divider } from '@mui/material';
import React from 'react';

import { DATA_TEST } from '../enums';
import { IContextSwitcherData, IEntityDetails } from '../types';

import { AppSwitcherListItems } from './AppSwitcher/AppSwitcherListItems';

import { ContextSwitcherMenuListContainer } from './ContextSwitcherMenuListContainer';

import { DesktopEntitySwitcherListItems } from './EntitySwitcher/DesktopEntitySwitcherListItems';

interface IContextSwitcherProps extends IContextSwitcherData {
  /**
   * @deprecated No-op on `ContextSwitcher`, which renders only the open entity and application menu. The NavBar
   * reads it to make the switcher trigger interactive (a button with a tooltip and a dropdown caret) where it would
   * otherwise be a static label: set `contextSwitcherOptions.forceContextSwitcherVisibility`.
   */
  forceContextSwitcherVisibility?: boolean;
  /**
   * @deprecated No-op on `ContextSwitcher`, which renders only the open entity and application menu. The NavBar
   * reads it to render the switcher trigger as a static label: set `contextSwitcherOptions.disabled`.
   */
  disabled?: boolean;
  /**
   * @deprecated No-op on `ContextSwitcher`, which renders only the open entity and application menu. The NavBar
   * sets it from its own context data fetch; while it is true the switcher trigger shows a spinner next to the
   * application name in place of the dropdown caret.
   */
  loading?: boolean;
  /**
   * @deprecated No-op on `ContextSwitcher`, which renders only the open entity and application menu. The NavBar
   * sets it from its own context data fetch and renders an error state instead of this menu.
   */
  loadingError?: boolean;
  firstContextSwitcherItemRef?: React.MutableRefObject<HTMLLIElement>;
  anchorEl?: HTMLDivElement;
  handleEntitySelect(entity: IEntityDetails): void;
  openEntitySelectionDialog(): void;
  openAppSelectionDialog(): void;
  handleClose?(): void;
}

/** The deprecated props above: the NavBar reads them, `ContextSwitcher` does not. */
const NAV_BAR_ONLY_PROPS = ['forceContextSwitcherVisibility', 'disabled', 'loading', 'loadingError'] as const;
type NavBarOnlyProps = Pick<IContextSwitcherProps, (typeof NAV_BAR_ONLY_PROPS)[number]>;

/** NavBar-only props already reported, so each is logged once rather than on every render. */
const reportedNavBarOnlyProps = new Set<string>();

/**
 * Forgets which props were reported, so the next one passed logs again. The record lives as long as the module, so
 * tests call this between cases; otherwise a case that passes every prop silences the log for the rest of the file.
 * Not exported from the package.
 */
export const resetReportedNavBarOnlyProps = () => reportedNavBarOnlyProps.clear();

/**
 * Logs, outside production and once per prop, each deprecated NavBar-only prop a caller passes, so a caller who
 * expected `loading` or `disabled` to change this menu learns where it belongs. It changes nothing on screen.
 */
const reportNavBarOnlyProps = (props: NavBarOnlyProps) => {
  if (process.env.NODE_ENV === 'production') return;
  NAV_BAR_ONLY_PROPS.forEach((name) => {
    if (props[name] === undefined || reportedNavBarOnlyProps.has(name)) return;
    reportedNavBarOnlyProps.add(name);
    console.error(
      `[components-nav-bar] \`ContextSwitcher\` ignores the deprecated \`${name}\` prop. The NavBar reads it from ` +
        'its context-switcher data; see the prop documentation.'
    );
  });
};

export const ContextSwitcher: React.FC<IContextSwitcherProps> = ({
  entitledEntities,
  activeEntity,
  activeProductKey,
  forcedDefaultApplication,
  firstContextSwitcherItemRef,
  anchorEl,
  onEntitySelect,
  handleEntitySelect,
  openEntitySelectionDialog,
  openAppSelectionDialog,
  handleClose,
  ...navBarOnlyProps
}) => {
  reportNavBarOnlyProps(navBarOnlyProps);

  return (
    <ContextSwitcherMenuListContainer
      dataTest={DATA_TEST.context_switcher_container}
      styleOverrides={{ maxWidth: 250, paddingBottom: 1, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}
    >
      <DesktopEntitySwitcherListItems
        firstContextSwitcherItemRef={firstContextSwitcherItemRef}
        anchorEl={anchorEl}
        activeEntity={activeEntity}
        entitledEntities={entitledEntities}
        handleEntitySelect={(entity) => {
          handleEntitySelect(entity);
          openAppSelectionDialog();
        }}
        handleSwitchEntitiesSelect={() => {
          handleEntitySelect(null);
          openEntitySelectionDialog();
        }}
        forcedDefaultApplication={forcedDefaultApplication}
        onEntitySelect={onEntitySelect}
        handleClose={handleClose}
      />
      {!forcedDefaultApplication && [
        <Divider component="li" aria-hidden="true" key="divider" />,
        <AppSwitcherListItems
          firstContextSwitcherItemRef={firstContextSwitcherItemRef}
          anchorEl={anchorEl}
          activeProductKey={activeProductKey}
          mobile={false}
          entitledApplications={activeEntity?.entitledApplications}
          autoFocusFirstItem={entitledEntities?.length < 2}
          handleClose={handleClose}
          key="app-switcher-list-items"
        />
      ]}
    </ContextSwitcherMenuListContainer>
  );
};
